/**
 * EidAdhaHomeBanner — modern editorial banner for Eid Al Adha.
 *
 * Composition philosophy: clean, modern, asymmetric. No heavy scene
 * (mountains / kaaba / drawn sun were dropped). The banner reads as
 * an "editorial card":
 *
 *   - soft tri-tone pastel gradient (amber / rose / emerald) as the
 *     base
 *   - three large blurred organic blob shapes for atmosphere
 *   - confetti dots scattered for a celebratory feel
 *   - bold modern Arabic headline + gold-emerald accent rule + greeting
 *     on one side
 *   - both real-photo sheep illustrations stacked as playful "stickers"
 *     on the other side, with subtle rotation, soft drop-shadow, and
 *     gentle bob
 *
 * Desktop is an asymmetric split (sheep left → headline right in RTL).
 * Mobile stacks: sheep on top, headline below.
 *
 * All decorative motion is gated behind `motion-safe:` so users with
 * `prefers-reduced-motion` see a static composition. Sheep are
 * `loading="eager"` because they're above the fold; the rest of the
 * scene is CSS-only (no extra network).
 */

import sheep1 from "@assets/images/sheep-1.png";
import sheep2 from "@assets/images/sheep-2.png";
import { cn } from "@design-system/lib/utils";

// ── Inline ornaments ─────────────────────────────────────────────────────

function Sparkle({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn("flex-shrink-0", className)}
      style={style}
      aria-hidden="true"
    >
      <path
        d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Crescent({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("flex-shrink-0", className)} aria-hidden="true">
      <defs>
        <mask id="adha-modern-crescent">
          <rect width="24" height="24" fill="white" />
          <circle cx="16" cy="12" r="9" fill="black" />
        </mask>
      </defs>
      <circle cx="12" cy="12" r="9" fill="currentColor" mask="url(#adha-modern-crescent)" />
    </svg>
  );
}

// ── Confetti dot config ──────────────────────────────────────────────────

type Dot = {
  /** Top-left position in % of the container */
  top: string;
  left: string;
  /** Tailwind size class — already paired so we can use bg-{color} */
  size: string;
  color: string;
  /** Pulse delay in seconds — staggers the blinks */
  delay: string;
  /** Ring shape (outline only) vs solid fill */
  ring?: boolean;
};

const DOTS: Dot[] = [
  { top: "12%", left: "8%",  size: "h-2 w-2",     color: "bg-amber-400",  delay: "0s"   },
  { top: "20%", left: "18%", size: "h-3 w-3",     color: "bg-emerald-500", delay: "0.4s", ring: true },
  { top: "70%", left: "10%", size: "h-2.5 w-2.5", color: "bg-rose-400",   delay: "1.2s" },
  { top: "35%", left: "26%", size: "h-1.5 w-1.5", color: "bg-sky-400",    delay: "0.8s" },
  { top: "85%", left: "22%", size: "h-2 w-2",     color: "bg-amber-300",  delay: "1.6s" },
  { top: "8%",  left: "48%", size: "h-2 w-2",     color: "bg-emerald-400", delay: "0.2s" },
  { top: "78%", left: "44%", size: "h-3 w-3",     color: "bg-amber-400",  delay: "1.0s", ring: true },
  { top: "16%", left: "60%", size: "h-2.5 w-2.5", color: "bg-rose-300",   delay: "0.6s" },
  { top: "82%", left: "62%", size: "h-2 w-2",     color: "bg-sky-300",    delay: "1.4s" },
  { top: "22%", left: "78%", size: "h-1.5 w-1.5", color: "bg-emerald-500", delay: "0.9s" },
  { top: "62%", left: "82%", size: "h-3 w-3",     color: "bg-amber-400",  delay: "1.8s" },
  { top: "10%", left: "92%", size: "h-2 w-2",     color: "bg-rose-400",   delay: "0.3s" },
  { top: "88%", left: "88%", size: "h-2.5 w-2.5", color: "bg-emerald-400", delay: "1.1s", ring: true },
];

// ── Main Component ─────────────────────────────────────────────────────────────

export function EidAdhaHomeBanner() {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-3xl",
        // Modern tri-tone pastel base — soft, airy, celebratory
        "bg-gradient-to-br from-amber-50 via-rose-50 to-emerald-50",
        "ring-1 ring-emerald-900/5",
        "shadow-2xl shadow-emerald-900/10",
      )}
      style={{ minHeight: "clamp(280px, 36vw, 380px)" }}
      dir="rtl"
    >
      {/* ─── Atmospheric blobs — three large blurred radial shapes ─── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-amber-200/60 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-1/3 h-80 w-80 rounded-full bg-rose-200/50 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-emerald-200/50 blur-3xl"
      />

      {/* ─── Confetti dots layer ─── */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        {DOTS.map((d, i) => (
          <span
            key={i}
            className={cn(
              "absolute rounded-full motion-safe:animate-pulse",
              d.size,
              d.ring ? `border-2 ${d.color.replace("bg-", "border-")}` : d.color,
            )}
            style={{
              top: d.top,
              left: d.left,
              animationDuration: "2.4s",
              animationDelay: d.delay,
            }}
          />
        ))}
      </div>

      {/* ─── Faint diagonal grid texture — adds quiet modernity ─── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "linear-gradient(45deg, #047857 1px, transparent 1px), linear-gradient(-45deg, #047857 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* ─── Main content grid — asymmetric on desktop, stacked on mobile ─── */}
      <div className="relative z-10 grid grid-cols-1 items-center gap-6 px-6 py-8 sm:gap-8 sm:px-10 sm:py-10 lg:grid-cols-[1fr_minmax(0,520px)] lg:gap-12 lg:px-16 lg:py-12">

        {/* ── Sheep cluster (mobile: top; desktop: leading column / left in RTL) ── */}
        <div className="relative order-first mx-auto flex h-32 w-full max-w-[340px] items-end justify-center sm:h-40 sm:max-w-[420px] lg:order-last lg:h-56 lg:max-w-none">

          {/* Soft ground shadow under the sheep cluster */}
          <div
            aria-hidden="true"
            className="absolute inset-x-8 bottom-2 h-3 rounded-[50%] bg-emerald-900/15 blur-md sm:bottom-3 sm:h-4 lg:bottom-4 lg:h-5"
          />

          {/* Profile sheep (sheep-2) — sits behind, slightly back-rotated */}
          <img
            src={sheep2}
            alt=""
            loading="eager"
            decoding="async"
            className={cn(
              "absolute bottom-1 right-2 h-28 w-auto -rotate-6",
              "drop-shadow-[0_10px_18px_rgba(6,95,70,0.18)]",
              "motion-safe:animate-[float-up_5s_ease-in-out_infinite_0.5s]",
              "sm:bottom-2 sm:h-36 lg:h-52",
            )}
            style={{ animationFillMode: "both" }}
          />

          {/* Front-facing chibi sheep (sheep-1) — overlaps in front, slight forward-rotate */}
          <img
            src={sheep1}
            alt=""
            loading="eager"
            decoding="async"
            className={cn(
              "absolute bottom-0 left-2 h-32 w-auto rotate-3",
              "drop-shadow-[0_12px_22px_rgba(6,95,70,0.22)]",
              "motion-safe:animate-[float-up_4.5s_ease-in-out_infinite]",
              "sm:left-6 sm:h-40 lg:h-56",
            )}
            style={{ animationFillMode: "both" }}
          />

          {/* Tiny floating sparkle accents around the cluster — desktop only,
              keeps mobile clean */}
          <Sparkle
            className={cn(
              "absolute right-0 top-2 hidden h-5 w-5 text-amber-400 sm:block",
              "motion-safe:animate-spin",
            )}
            style={{ animationDuration: "8s" }}
          />
          <Sparkle
            className={cn(
              "absolute left-2 top-6 hidden h-4 w-4 text-emerald-500 sm:block",
              "motion-safe:animate-spin [animation-direction:reverse]",
            )}
            style={{ animationDuration: "10s" }}
          />
          <Sparkle
            className={cn(
              "absolute right-12 top-16 hidden h-3 w-3 text-rose-400 sm:block",
              "motion-safe:animate-spin",
            )}
            style={{ animationDuration: "6s", animationDelay: "1s" }}
          />
        </div>

        {/* ── Text column ── */}
        <div className="relative flex flex-col items-center text-center lg:items-start lg:text-start">

          {/* Tiny pre-headline label */}
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-emerald-100/70 px-3 py-1 backdrop-blur-sm sm:mb-4">
            <Crescent className="h-3 w-3 text-emerald-700" />
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-800 sm:text-xs"
              style={{ fontFamily: '"Inter", "Segoe UI", sans-serif' }}
            >
              Eid Al Adha · 1447
            </span>
            <Sparkle className="h-2.5 w-2.5 text-amber-500" />
          </div>

          {/* Bold modern Arabic headline */}
          <h2
            className={cn(
              "text-4xl font-extrabold leading-[1.15] tracking-tight text-emerald-900",
              "sm:text-5xl lg:text-6xl",
            )}
            style={{
              fontFamily: '"Noto Naskh Arabic", "Amiri", "Segoe UI", Tahoma, serif',
            }}
          >
            عيد الأضحى المبارك
          </h2>

          {/* Gold-to-emerald accent rule */}
          <div className="mt-4 flex items-center gap-2 sm:mt-5">
            <div className="h-[3px] w-12 rounded-full bg-gradient-to-r from-amber-400 to-emerald-500 sm:w-20" />
            <Sparkle className="h-3 w-3 text-amber-500" />
            <div className="h-[3px] w-6 rounded-full bg-emerald-300 sm:w-10" />
          </div>

          {/* Greeting */}
          <p
            className={cn(
              "mt-4 max-w-md text-sm leading-relaxed text-emerald-800/85",
              "sm:mt-5 sm:text-base lg:text-lg",
            )}
            style={{
              fontFamily: '"Noto Naskh Arabic", "Amiri", "Segoe UI", Tahoma, serif',
            }}
          >
            تقبّل الله منا ومنكم صالح الأعمال — حج مبرور وذنب مغفور، وكل عام وأنتم بخير
          </p>
        </div>
      </div>
    </div>
  );
}
